import React, { useMemo, useState } from "react";
import PublicLegalLayout from "./PublicLegalLayout";
import { SUPPORT_EMAIL, SUPPORT_MAILTO, SUPPORT_FAQ } from "../../lib/legalCopy";

/**
 * Public, no-auth-required support page. Required by Apple App Review
 * Guideline 1.5 — the Support URL listed in App Store Connect must direct
 * users to a working page that explains how to ask questions and get help.
 *
 * Lives at `/support` (preview env + production tiplingo.com after deploy).
 * Mirrors the visual chrome of /about, /terms, /privacy. FAQ content is
 * shared with the in-app Settings → Help center via `SUPPORT_FAQ` in
 * `legalCopy.js` so the two surfaces never drift apart.
 */
export default function SupportPage() {
  const [q, setQ] = useState("");
  // Case-insensitive haystack across question + answer. Two-pass match:
  //   1. Exact full-phrase substring (handles single keywords like "refund",
  //      "cancel", "vault" — fastest, most precise).
  //   2. Natural-language fallback — tokenize on whitespace, drop filler
  //      words shorter than 4 characters, match any FAQ containing any
  //      meaningful token. Lets queries like "how much is the monthly
  //      subscription" land on the pricing / cancellation FAQs instead of
  //      returning empty.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return SUPPORT_FAQ;
    const exact = SUPPORT_FAQ.filter(
      (item) =>
        item.q.toLowerCase().includes(needle) ||
        item.a.toLowerCase().includes(needle),
    );
    if (exact.length > 0) return exact;
    const tokens = needle.split(/\s+/).filter((t) => t.length >= 4);
    if (tokens.length === 0) return [];
    return SUPPORT_FAQ.filter((item) => {
      const hay = (item.q + " " + item.a).toLowerCase();
      return tokens.some((t) => hay.includes(t));
    });
  }, [q]);
  return (
    <PublicLegalLayout title="TipLingo Support">
      {/* Primary contact card — front-and-center so Apple Reviewer sees it
          immediately on page load. */}
      <section
        data-testid="support-contact-hero"
        className="bg-navy text-white rounded-3xl shadow-deep p-6 sm:p-8"
      >
        <h2 className="font-jakarta font-extrabold text-2xl mb-2">
          Need help? Email us.
        </h2>
        <p className="text-white/80 text-base mb-5 max-w-xl">
          TipLingo is built and supported by a small team. Every email is
          read by a real person, and we usually reply within one business
          day.
        </p>
        <a
          href={SUPPORT_MAILTO}
          data-testid="support-email-cta"
          className="inline-flex items-center justify-center rounded-full bg-orange hover:bg-orange-hover text-white font-jakarta font-extrabold text-base px-6 py-3.5"
        >
          Email {SUPPORT_EMAIL}
        </a>
        <p className="text-white/60 text-xs mt-4">
          Please include your device (iPhone model + iOS version) and a brief
          description of the issue. Screenshots help us debug faster.
        </p>
      </section>

      {/* FAQ — common questions answered inline so users (and reviewers)
          can self-serve without writing in. */}
      <section
        data-testid="support-faq"
        className="bg-white rounded-3xl shadow-soft border border-navy/5 p-6 sm:p-7"
      >
        <h2 className="font-jakarta font-extrabold text-2xl text-navy mb-4">
          Frequently asked questions
        </h2>
        <div className="relative mb-5">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the FAQ — try “cancel”, “refund”, “sign in”…"
            data-testid="support-faq-search"
            className="w-full bg-bone border border-navy/10 rounded-full pl-11 pr-4 py-3 text-sm text-navy font-jakarta placeholder:text-navy-soft focus:outline-none focus:ring-2 focus:ring-orange/40 focus:border-orange/40"
          />
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-soft"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
        </div>
        {filtered.length === 0 ? (
          <div
            data-testid="support-faq-empty"
            className="text-navy-soft text-sm py-3"
          >
            No FAQ matches “{q}”. Try a different keyword, or{" "}
            <a
              href={SUPPORT_MAILTO}
              className="text-orange font-bold hover:underline"
            >
              email {SUPPORT_EMAIL}
            </a>
            .
          </div>
        ) : (
          <div className="space-y-5">
            {filtered.map((item) => {
              const idx = SUPPORT_FAQ.indexOf(item);
              return (
                <div
                  key={idx}
                  data-testid={`support-faq-item-${idx}`}
                  className="border-b border-navy/5 pb-5 last:border-0 last:pb-0"
                >
                  <h3 className="font-jakarta font-extrabold text-navy text-base mb-1.5">
                    {item.q}
                  </h3>
                  <p className="text-navy/80 text-sm leading-relaxed">
                    {item.a}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Subscription management quick-links — Apple specifically wants
          users to be able to find how to cancel. */}
      <section
        data-testid="support-subscriptions"
        className="bg-white rounded-3xl shadow-soft border border-navy/5 p-6 sm:p-7"
      >
        <h2 className="font-jakarta font-extrabold text-2xl text-navy mb-3">
          Manage your subscription
        </h2>
        <ul className="space-y-2.5 text-navy text-sm">
          <li className="flex items-start gap-2.5">
            <span
              aria-hidden
              className="mt-2 w-1.5 h-1.5 rounded-full bg-orange shrink-0"
            />
            <span>
              <strong className="font-jakarta font-bold">iOS:</strong>{" "}
              Settings app → your Apple ID name → Subscriptions → TipLingo →
              Cancel Subscription.
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <span
              aria-hidden
              className="mt-2 w-1.5 h-1.5 rounded-full bg-orange shrink-0"
            />
            <span>
              <strong className="font-jakarta font-bold">Web:</strong>{" "}
              Open TipLingo → Account → Manage subscription (opens the
              customer portal). Cancel, change card, or download invoices.
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <span
              aria-hidden
              className="mt-2 w-1.5 h-1.5 rounded-full bg-orange shrink-0"
            />
            <span>
              <strong className="font-jakarta font-bold">Refunds:</strong>{" "}
              iOS purchases —{" "}
              <a
                href="https://reportaproblem.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange font-bold hover:underline"
              >
                reportaproblem.apple.com
              </a>
              . Web purchases — email{" "}
              <a
                href={SUPPORT_MAILTO}
                className="text-orange font-bold hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </span>
          </li>
        </ul>
      </section>

      {/* Direct contact fallback at the bottom for reviewers / users who
          scroll past everything. */}
      <section
        data-testid="support-contact-footer"
        className="bg-bone rounded-3xl border border-navy/10 p-6 sm:p-7"
      >
        <h2 className="font-jakarta font-extrabold text-xl text-navy mb-2">
          Still need help?
        </h2>
        <p className="text-navy/80 text-sm mb-4">
          Reach out directly — we want to hear from you.
        </p>
        <a
          href={SUPPORT_MAILTO}
          data-testid="support-email-cta-footer"
          className="inline-flex items-center justify-center rounded-full bg-orange hover:bg-orange-hover text-white font-jakarta font-extrabold text-sm px-5 py-3"
        >
          Email {SUPPORT_EMAIL}
        </a>
      </section>
    </PublicLegalLayout>
  );
}
